import { Injectable, computed, signal } from '@angular/core';
import { AppSession, LoginApiResponse } from '../models/session.models';

const SESSION_STORAGE_KEY = 'flwx_session';
const LAST_COMPANY_KEY = 'flwx_last_selected_company_id';


@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private readonly sessionSignal = signal<AppSession | null>(this.readSessionFromStorage());

  readonly session = computed(() => this.sessionSignal());
  readonly isLoggedIn = computed(() => !!this.sessionSignal()?.accessToken);
  readonly user = computed(() => this.sessionSignal()?.user ?? null);
  readonly token = computed(() => this.sessionSignal()?.accessToken ?? '');
  readonly roleCode = computed(() => this.sessionSignal()?.user.roleCode ?? '');
  readonly isSuperAdmin = computed(() => this.sessionSignal()?.user.roleCode === 'SUPER_ADMIN');
  readonly isCompanyAdmin = computed(() => this.sessionSignal()?.user.roleCode === 'COMPANY_ADMIN');
  readonly selectedCompanyId = computed(() => this.sessionSignal()?.selectedCompanyId ?? 1);


  constructor() {
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === SESSION_STORAGE_KEY) {
        this.sessionSignal.set(this.readSessionFromStorage());
      }
    });
  }

  startSession(response: LoginApiResponse): void {
    const user = response.data.user;

    let initialSelectedCompanyId = user.companyId;

    if (user.roleCode === 'SUPER_ADMIN') {
      const lastId = localStorage.getItem(LAST_COMPANY_KEY);
      initialSelectedCompanyId = lastId ? parseInt(lastId, 10) : 1;
    }


    const session: AppSession = {
      accessToken: response.data.accessToken,
      user,
      selectedCompanyId: initialSelectedCompanyId
    };

    this.persistSession(session);
  }

  updateSelectedCompany(companyId: number): void {
    const current = this.sessionSignal();
    if (!current) return;

    let nextCompanyId =
      current.user.roleCode === 'SUPER_ADMIN' ? companyId : current.user.companyId;

    if (current.user.roleCode === 'SUPER_ADMIN') {
      localStorage.setItem(LAST_COMPANY_KEY, nextCompanyId.toString());
    }


    const updated: AppSession = {
      ...current,
      selectedCompanyId: nextCompanyId
    };

    this.persistSession(updated);
  }

  clearSession(): void {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    this.sessionSignal.set(null);
  }

  private persistSession(session: AppSession): void {
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    this.sessionSignal.set(session);
  }

  private readSessionFromStorage(): AppSession | null {
    try {
      const raw = localStorage.getItem(SESSION_STORAGE_KEY);
      if (!raw) return null;

      return JSON.parse(raw) as AppSession;
    } catch {
      return null;
    }
  }
}